import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Image, Palette, FileText, Building, Upload, Trash2, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  getPersonaSettings,
  updatePersonaSettings,
  type PersonaSettingsView,
  type PersonaSettingsInput,
} from '../../../api/PublicProfile/publicProfileApi';
import { useFileUploadLimits } from '../../../hooks/useFileUploadLimits';

interface ColorPreset {
  name: string;
  primary: string;
  secondary: string;
}

interface BrandingTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

export interface BrandingTabRef {
  save: () => Promise<boolean>;
  hasChanges: () => boolean;
}

const BrandingTab = forwardRef<BrandingTabRef, BrandingTabProps>(({ selectedPersonaId, onSettingsChange }, ref) => {
  const { t } = useTranslation();
  const { validateFileSize } = useFileUploadLimits();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [settings, setSettings] = useState<PersonaSettingsView | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Local state for form fields
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#6366F1');
  const [disclaimerText, setDisclaimerText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [showCompanyLogo, setShowCompanyLogo] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const loadData = useCallback(async () => {
    if (!selectedPersonaId) {
      setSettings(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasChanges(false); // Reset changes when loading new persona
    try {
      const personaId = parseInt(selectedPersonaId, 10);
      const result = await getPersonaSettings(personaId);

      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
        setCompanyLogoUrl(result.payload.companyLogoUrl || null);
        setLogoPreview(result.payload.companyLogoUrl || null);
        setPrimaryColor(result.payload.primaryColor || '#3B82F6');
        setSecondaryColor(result.payload.secondaryColor || '#6366F1');
        setDisclaimerText(result.payload.disclaimerText || '');
        setFooterText(result.payload.footerText || '');
        setShowCompanyLogo(result.payload.showCompanyLogo);
        setShowDisclaimer(result.payload.showDisclaimer);
      } else {
        showToast('error', t('Failed to load branding settings'));
      }
    } catch (error) {
      console.error('Error loading branding settings:', error);
      showToast('error', t('Failed to load branding settings'));
    } finally {
      setLoading(false);
    }
  }, [selectedPersonaId, t]);

  // Reload data when selectedPersonaId changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!hasChanges) return true;
      return await handleSaveChanges();
    },
    hasChanges: () => hasChanges,
  }));

  // Helper to mark changes and notify parent
  const markAsChanged = () => {
    setHasChanges(true);
    onSettingsChange?.();
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const { valid, maxMB } = validateFileSize(file, 'companyLogoMaxSizeMB');
      if (!valid) {
        showToast('error', t('fileSizeExceedsLimit', { name: file.name, maxMB }));
        if (e.target) e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setLogoPreview(base64);
        setCompanyLogoUrl(base64);
        markAsChanged();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setCompanyLogoUrl(null);
    markAsChanged();
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    if (!settings || !selectedPersonaId) return false;

    setSaving(true);
    try {
      const personaId = parseInt(selectedPersonaId, 10);
      const input: PersonaSettingsInput = {
        personaId,
        companyLogoUrl: companyLogoUrl || null,
        primaryColor,
        secondaryColor,
        disclaimerText: disclaimerText || null,
        footerText: footerText || null,
        showCompanyLogo,
        showDisclaimer,
        // Preserve control settings
        allowEmployeeToggle: settings.allowEmployeeToggle,
        requiresApproval: settings.requiresApproval,
        allowSlugRegeneration: settings.allowSlugRegeneration,
      };

      const result = await updatePersonaSettings(input);
      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
        setHasChanges(false);
        showToast('success', t('Branding settings saved successfully'));
        onSettingsChange?.();
        return true;
      } else {
        showToast('error', t('Failed to save branding settings'));
        return false;
      }
    } catch (error) {
      console.error('Error saving branding settings:', error);
      showToast('error', t('Failed to save branding settings'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const colorPresets: ColorPreset[] = [
    { name: 'Blue', primary: '#3B82F6', secondary: '#6366F1' },
    { name: 'Green', primary: '#10B981', secondary: '#059669' },
    { name: 'Purple', primary: '#8B5CF6', secondary: '#7C3AED' },
    { name: 'Red', primary: '#EF4444', secondary: '#DC2626' },
    { name: 'Orange', primary: '#F59E0B', secondary: '#D97706' },
    { name: 'Pink', primary: '#EC4899', secondary: '#DB2777' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ms-3 text-muted-foreground">{t('Loading branding settings...')}</span>
      </div>
    );
  }

  if (!selectedPersonaId) {
    return (
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-warning" />
          <p className="text-sm text-warning">
            {t('Please select a persona to configure branding settings.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Persona Info Banner */}
      {selectedPersonaId && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            <p className="text-sm text-primary">
              {t('You are configuring branding settings for the selected persona. Changes will only apply to this persona.')}
            </p>
          </div>
        </div>
      )}

      {/* Company Logo */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Image className="w-5 h-5 me-2" />
            {t('Company Logo')}
          </h2>

          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {/* Preview on a colored gradient that mirrors the actual public
                  profile header, so HR can see how the logo will look against
                  a real persona-style backdrop instead of a plain white card. */}
              {logoPreview ? (
                <div className="w-32 h-32 bg-gradient-to-br from-blue-600 to-indigo-600 border border-border rounded-lg flex items-center justify-center p-1.5 shadow-inner">
                  <div className="bg-white/15 backdrop-blur-md rounded-md p-2 ring-1 ring-white/25 shadow-lg w-full h-full flex items-center justify-center">
                    <img
                      src={logoPreview}
                      alt={t('Company logo')}
                      className="max-w-full max-h-full object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-32 h-32 bg-muted rounded-lg flex items-center justify-center">
                  <Building className="w-12 h-12 text-muted-foreground" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">
                {t('Upload your company logo to appear on all public employee profiles.')}
              </p>
              {/* Optimal-dimensions guidance — surfaces best practice up-front
                  so HR avoids uploading thin banners or tall portraits that
                  shrink badly inside the public profile's compact logo slot. */}
              <div
                role="note"
                data-testid="logo-dimensions-hint"
                className="mb-4 p-3 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-2"
              >
                <Info className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                <div className="text-xs text-foreground">
                  <p className="font-semibold mb-1">{t('Optimal logo dimensions')}</p>
                  <ul className="list-disc ms-5 space-y-0.5 text-muted-foreground">
                    <li>{t('Aspect ratio: 1:1 (square) or up to 3:1 (horizontal banner)', { nsSeparator: false })}</li>
                    <li>{t('Recommended size: 512 × 512 px (square) or 600 × 200 px (banner)', { nsSeparator: false })}</li>
                    <li>{t('Format: PNG (transparent background) or SVG', { nsSeparator: false })}</li>
                    <li>{t('Tip: a transparent background keeps the logo readable regardless of the persona color scheme', { nsSeparator: false })}</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 me-2" />
                      {t('Upload Logo')}
                    </span>
                  </Button>
                </label>
                {logoPreview && (
                  <Button variant="outline" onClick={handleRemoveLogo} iconName="Trash2">
                    {t('Remove')}
                  </Button>
                )}
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showCompanyLogo}
                    onChange={(e) => {
                      setShowCompanyLogo(e.target.checked);
                      markAsChanged();
                    }}
                    className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t('Show company logo on public profiles')}</span>
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Scheme */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Palette className="w-5 h-5 me-2" />
            {t('Color Scheme')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">{t('Color Presets')}</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      setPrimaryColor(preset.primary);
                      setSecondaryColor(preset.secondary);
                      markAsChanged();
                    }}
                    className={`p-3 border-2 rounded-lg hover:border-primary transition-colors ${
                      primaryColor === preset.primary && secondaryColor === preset.secondary
                        ? 'border-primary'
                        : 'border-border'
                    }`}
                  >
                    <div className="flex gap-1 mb-2">
                      <div className="w-8 h-8 rounded" style={{ backgroundColor: preset.primary }}></div>
                      <div className="w-8 h-8 rounded" style={{ backgroundColor: preset.secondary }}></div>
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">{t(preset.name)}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Primary Color')}</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => {
                      setPrimaryColor(e.target.value);
                      markAsChanged();
                    }}
                    className="w-12 h-10 rounded border border-input cursor-pointer"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => {
                      setPrimaryColor(e.target.value);
                      markAsChanged();
                    }}
                    className="flex-1 px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Secondary Color')}</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => {
                      setSecondaryColor(e.target.value);
                      markAsChanged();
                    }}
                    className="w-12 h-10 rounded border border-input cursor-pointer"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => {
                      setSecondaryColor(e.target.value);
                      markAsChanged();
                    }}
                    className="flex-1 px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm font-medium text-foreground mb-3">{t('Preview')}</p>
              <div className="flex gap-3">
                <div
                  className="flex-1 h-20 rounded-lg flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('Primary')}
                </div>
                <div
                  className="flex-1 h-20 rounded-lg flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: secondaryColor }}
                >
                  {t('Secondary')}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legal & Disclaimer */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <FileText className="w-5 h-5 me-2" />
            {t('Legal & Disclaimer')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Disclaimer Text')}</label>
              <textarea
                value={disclaimerText}
                onChange={(e) => {
                  setDisclaimerText(e.target.value);
                  markAsChanged();
                }}
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                placeholder={t('Enter disclaimer text...')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Footer Text')}</label>
              <textarea
                value={footerText}
                onChange={(e) => {
                  setFooterText(e.target.value);
                  markAsChanged();
                }}
                rows={2}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                placeholder={t('Enter footer text...')}
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showDisclaimer}
                  onChange={(e) => {
                    setShowDisclaimer(e.target.checked);
                    markAsChanged();
                  }}
                  className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
                />
                <span className="text-sm text-foreground">{t('Show disclaimer on all public profiles')}</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
});

BrandingTab.displayName = 'BrandingTab';

export default BrandingTab;
